import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "@/lib/api";

const STORAGE_KEY = "samara_chat_session";
const SUGGESTED = [
  "What is SAMARA?",
  "Tell me about the business plan",
  "Show me starter packages",
  "How does binary matching work?",
  "How do I become a distributor?",
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [shrunk, setShrunk] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const scrollRef = useRef(null);
  const shrinkTimer = useRef(null);

  useEffect(() => {
    if (sessionId) localStorage.setItem(STORAGE_KEY, sessionId);
  }, [sessionId]);

  // Idle-shrink: when the closed button sits idle for 3s, collapse to a circle
  useEffect(() => {
    const clear = () => { if (shrinkTimer.current) { clearTimeout(shrinkTimer.current); shrinkTimer.current = null; } };
    if (open) { clear(); setShrunk(false); return clear; }
    clear();
    shrinkTimer.current = setTimeout(() => setShrunk(true), 3000);
    return clear;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: "Hi 👋 I'm Samara Saathi — your guide to SAMARA's Health · Education · Beauty platform. Ask me about products, packages, the business plan, or how to join.",
      }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: msg });
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't reach Samara just now. Please try again in a moment, or email support@samarawiz.com.", error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button — bottom-right. Idle-shrinks to a circle after 3s. */}
      {!open && (
        <button
          data-testid="chatbot-open-btn"
          onClick={() => setOpen(true)}
          onMouseEnter={() => setShrunk(false)}
          onMouseLeave={() => {
            if (shrinkTimer.current) clearTimeout(shrinkTimer.current);
            shrinkTimer.current = setTimeout(() => setShrunk(true), 1500);
          }}
          onFocus={() => setShrunk(false)}
          aria-label="Open Samara assistant"
          className={`fixed bottom-5 right-5 z-[60] group bg-navy text-gold border border-gold/30 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 ease-out inline-flex items-center gap-2 ${
            shrunk
              ? "w-12 h-12 rounded-full px-0 justify-center"
              : "h-12 rounded-full px-4"
          }`}
        >
          <Sparkles size={16} className={`text-gold ${shrunk ? "animate-pulse" : ""}`} />
          <span
            data-testid="chatbot-open-label"
            className={`text-xs uppercase tracking-widest font-medium overflow-hidden transition-all duration-300 ease-out ${
              shrunk ? "max-w-0 opacity-0" : "max-w-[140px] opacity-100"
            }`}
          >
            Ask Samara
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-[60] w-[92vw] max-w-[380px] h-[70vh] max-h-[600px] bg-white border border-navy/15 rounded-sm shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-navy text-white px-4 py-3 flex items-center justify-between border-b border-gold/20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/15 grid place-items-center">
                <Sparkles size={14} className="text-gold" />
              </div>
              <div>
                <div className="font-serif text-base leading-tight">Samara Saathi</div>
                <div className="text-[10px] uppercase tracking-widest text-gold/70">Health · Education · Beauty</div>
              </div>
            </div>
            <button
              data-testid="chatbot-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="text-white/70 hover:text-white p-1"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-cream/40">
            {messages.map((m, i) => (
              <div
                key={`msg-${i}-${m.role}`}
                data-testid={`chatbot-msg-${m.role}`}
                className={`max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap rounded-sm px-3 py-2 ${
                  m.role === "user"
                    ? "ml-auto bg-navy text-white"
                    : m.error
                      ? "bg-rose/10 text-rose border border-rose/20"
                      : "bg-white border border-navy/10 text-navy"
                }`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="bg-white border border-navy/10 text-navy/60 rounded-sm px-3 py-2 text-sm inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "120ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: "240ms" }} />
              </div>
            )}
          </div>

          {/* Suggested prompts (only on first turn) */}
          {messages.length <= 1 && !busy && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5 border-t border-navy/5 pt-3 bg-white">
              {SUGGESTED.map((s, i) => (
                <button
                  key={s}
                  data-testid={`chatbot-suggested-${i}`}
                  onClick={() => send(s)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-gold/30 text-navy hover:bg-gold/10 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-navy/10 px-3 py-3 bg-white flex items-end gap-2">
            <textarea
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about products, plan, packages…"
              rows={1}
              className="flex-1 resize-none text-sm border border-navy/15 focus:border-gold focus:outline-none rounded-sm px-3 py-2 max-h-24"
            />
            <button
              data-testid="chatbot-send-btn"
              onClick={() => send()}
              disabled={!input.trim() || busy}
              className="bg-gold text-navy w-10 h-10 rounded-sm grid place-items-center disabled:opacity-40 hover:bg-gold-deep transition-colors"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
